import React, { useCallback } from "react";
import classNames from "classnames";
import { currencyFormatterShort } from "@common/helpers/currency";
import { TRIAL_LENGTH_DAYS } from "@common/flatRateBilling";
import { useBillingManagement } from "@hooks/useBillingManagement";
import { useBillingStatus } from "@hooks/useBillingStatus";
import { Button } from "@design-system/Button/Button";
import { EndTrialAction } from "../billing/AccessBlockedPage/EndTrialAction";
import { PlanManagementProvider } from "../billing/PlanManagement/PlanManagementProvider";
import { useSettings } from "../settings/SettingsProvider";
import styles from "./ViewTableOverlay.module.scss";

interface ViewTableOverlayProps {
    header?: React.ReactNode;
    description?: React.ReactNode;
    position?: "top" | "bottom";
    className?: string;
}

const createDefaultHeader = (currentUsageLimit: number) => (
    <>
        <h3>
            You've reached the{" "}
            {currencyFormatterShort.format(currentUsageLimit).toLowerCase()}{" "}
            expense limit
        </h3>
        <p>
            Start your {TRIAL_LENGTH_DAYS}-day trial now to access recent data
        </p>
    </>
);

export const ViewTableOverlay: React.FC<ViewTableOverlayProps> = ({
    header,
    description,
    position = "top",
    className,
}) => {
    const { isTrialing } = useBillingStatus();
    const { currentUsageLimit } = useBillingManagement();
    const { open } = useSettings();

    const handleOpenPlans = useCallback(() => open(["plans"]), [open]);

    const displayHeader = header ?? createDefaultHeader(currentUsageLimit);
    const actionButton = isTrialing ? (
        <PlanManagementProvider>
            <EndTrialAction variant="tertiary">
                Start subscription
            </EndTrialAction>
        </PlanManagementProvider>
    ) : (
        <Button
            onClick={handleOpenPlans}
            variant="tertiary"
            size="md"
            className={styles.upgradeButton}
        >
            Upgrade
        </Button>
    );

    return (
        <div
            className={classNames(
                styles.overlay,
                {
                    [styles.top]: position === "top",
                    [styles.bottom]: position === "bottom",
                },
                className,
            )}
        >
            <div className={styles.content}>
                <div className={styles.header}>{displayHeader}</div>
                {description && (
                    <div className={styles.description}>{description}</div>
                )}
                <div className={styles.action}>{actionButton}</div>
            </div>
        </div>
    );
};
